import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppStore } from '../lib/store'
import { LogOut, Calendar, Users, TrendingUp, DollarSign, CheckCircle, Clock, Briefcase, Bell, Settings, Star, Award, Target } from 'lucide-react'
import { toast } from '../lib/toast'
import ProviderSchedule from './ProviderSchedule'
import ProviderClients from './ProviderClients'
import ProviderPerformance from './ProviderPerformance'
import ThemeToggle from './ThemeToggle'

interface ProviderJob {
  id: string
  clientName: string
  service: string
  date: string
  time: string
  duration: string
  amount: number
  status: 'completed' | 'in-progress' | 'pending'
}

export default function ProviderDashboard() {
  const { user, setUser } = useAppStore()
  const navigate = useNavigate()
  const [currentView, setCurrentView] = React.useState<'dashboard' | 'schedule' | 'clients' | 'performance'>('dashboard')

  const handleLogout = () => {
    setUser(null)
    navigate('/')
  }

  // Mock data for provider stats
  const providerStats = {
    totalEarnings: 15420,
    monthlyEarnings: 3280,
    completedJobs: 47,
    pendingJobs: 3,
    averageRating: 4.8,
    totalReviews: 128,
    activeClients: 12,
    completionRate: 94,
    totalHours: 156
  }

  // Mock recent jobs data
  const recentJobs: ProviderJob[] = [
    {
      id: '1',
      clientName: 'Sarah Johnson',
      service: 'Deep Cleaning',
      date: '2024-03-15',
      time: '10:00 AM',
      duration: '3',
      amount: 180,
      status: 'completed'
    },
    {
      id: '2',
      clientName: 'Mike Chen',
      service: 'Office Cleaning',
      date: '2024-03-14',
      time: '2:00 PM',
      duration: '2',
      amount: 120,
      status: 'completed'
    },
    {
      id: '3',
      clientName: 'Emily Davis',
      service: 'Regular Cleaning',
      date: '2024-03-13',
      time: '9:00 AM',
      duration: '2.5',
      amount: 100,
      status: 'in-progress'
    },
    {
      id: '4',
      clientName: 'Robert Wilson',
      service: 'Move-in Cleaning',
      date: '2024-03-12',
      time: '11:00 AM',
      duration: '4',
      amount: 240,
      status: 'completed'
    },
    {
      id: '5',
      clientName: 'Lisa Anderson',
      service: 'Post-Construction',
      date: '2024-03-11',
      time: '1:00 PM',
      duration: '5',
      amount: 350,
      status: 'completed'
    },
    {
      id: '6',
      clientName: 'James Taylor',
      service: 'Window Cleaning',
      date: '2024-03-10',
      time: '3:00 PM',
      duration: '1.5',
      amount: 90,
      status: 'pending'
    }
  ]

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'in-progress':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const renderCurrentView = () => {
    switch (currentView) {
      case 'schedule':
        return <ProviderSchedule />
      case 'clients':
        return <ProviderClients />
      case 'performance':
        return <ProviderPerformance />
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      {/* Header */}
      <header className="bg-white dark:bg-dark-card shadow-sm border-b border-gray-200 dark:border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Briefcase className="w-6 h-6 text-green-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-dark-text-primary">Provider Dashboard</h1>
                <p className="text-sm text-gray-500 dark:text-dark-text-secondary">Welcome back, {user?.email}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <ThemeToggle />
              <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
                <Bell className="w-5 h-5" />
              </button>
              <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
                <Settings className="w-5 h-5" />
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-1 md:space-x-2 bg-orange-200 text-orange-800 px-2 md:px-4 py-1.5 md:py-2 rounded-lg hover:bg-orange-300 transition-colors text-xs md:text-sm"
              >
                <LogOut className="w-3 h-3 md:w-4 md:h-4" />
                <span className="hidden md:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white dark:bg-dark-card border-b border-gray-200 dark:border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setCurrentView('dashboard')}
              className={`py-4 px-2 border-b-2 transition-colors ${
                currentView === 'dashboard' 
                  ? 'border-green-600 text-green-600' 
                  : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => setCurrentView('schedule')}
              className={`py-4 px-2 border-b-2 transition-colors ${
                currentView === 'schedule' 
                  ? 'border-green-600 text-green-600' 
                  : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
              }`}
            >
              Schedule
            </button>
            <button
              onClick={() => setCurrentView('clients')}
              className={`py-4 px-2 border-b-2 transition-colors ${
                currentView === 'clients' 
                  ? 'border-green-600 text-green-600' 
                  : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
              }`}
            >
              Clients
            </button>
            <button
              onClick={() => setCurrentView('performance')}
              className={`py-4 px-2 border-b-2 transition-colors ${
                currentView === 'performance' 
                  ? 'border-green-600 text-green-600' 
                  : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
              }`}
            >
              Performance
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {currentView === 'dashboard' ? (
          <>
            {/* Provider Stats Overview */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-6 mb-8">
              <div className="bg-white dark:bg-dark-card p-2 md:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-gray-600 dark:text-dark-text-secondary mb-1">Earnings</p>
                    <p className="text-lg md:text-2xl font-bold text-green-600">${providerStats.totalEarnings.toLocaleString()}</p>
                    <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-1 hidden md:block">This month: ${providerStats.monthlyEarnings}</p>
                  </div>
                  <DollarSign className="w-4 h-4 md:w-8 md:h-8 text-green-500" />
                </div>
              </div>

              <div className="bg-white dark:bg-dark-card p-2 md:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-gray-600 dark:text-dark-text-secondary mb-1">Jobs</p>
                    <p className="text-lg md:text-2xl font-bold text-blue-600">{providerStats.completedJobs}</p>
                    <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-1 hidden md:block">{providerStats.pendingJobs} pending</p>
                  </div>
                  <CheckCircle className="w-4 h-4 md:w-8 md:h-8 text-blue-500" />
                </div>
              </div>

              <div className="bg-white dark:bg-dark-card p-2 md:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-gray-600 dark:text-dark-text-secondary mb-1">Rating</p>
                    <p className="text-lg md:text-2xl font-bold text-yellow-600">{providerStats.averageRating}/5</p>
                    <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-1 hidden md:block">{providerStats.totalReviews} reviews</p>
                  </div>
                  <Star className="w-4 h-4 md:w-8 md:h-8 text-yellow-500" />
                </div>
              </div>

              <div className="bg-white dark:bg-dark-card p-2 md:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-gray-600 dark:text-dark-text-secondary mb-1">Clients</p>
                    <p className="text-lg md:text-2xl font-bold text-purple-600">{providerStats.activeClients}</p>
                    <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-1 hidden md:block">Active clients</p>
                  </div>
                  <Users className="w-4 h-4 md:w-8 md:h-8 text-purple-500" />
                </div>
              </div>
            </div>

            {/* Performance Metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-2 gap-2 md:gap-6 mb-8">
              <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-2 md:p-6">
                <h2 className="text-xs md:text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-2 md:mb-4 flex items-center">
                  <TrendingUp className="w-3 h-3 md:w-5 md:h-5 mr-1 md:mr-2 text-green-600" />
                  <span className="hidden md:inline">Earnings Overview</span>
                  <span className="md:hidden">Earnings</span>
                </h2>
                <div className="space-y-1 md:space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-xs md:text-sm text-gray-600 dark:text-dark-text-secondary">This Month</span>
                    <span className="font-semibold text-green-600 dark:text-emerald-400 text-sm md:text-lg">${providerStats.monthlyEarnings}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs md:text-sm text-gray-600 dark:text-dark-text-secondary">Last Month</span>
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400 text-sm md:text-lg">$2,850</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs md:text-sm text-gray-600 dark:text-dark-text-secondary">Avg/Job</span>
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400 text-sm md:text-lg">
                      ${Math.round(providerStats.totalEarnings / providerStats.completedJobs)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-2 md:p-6">
                <h2 className="text-xs md:text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-2 md:mb-4 flex items-center">
                  <Clock className="w-3 h-3 md:w-5 md:h-5 mr-1 md:mr-2 text-blue-600" />
                  <span className="hidden md:inline">Work Statistics</span>
                  <span className="md:hidden">Work</span>
                </h2>
                <div className="space-y-1 md:space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-xs md:text-sm text-gray-600 dark:text-dark-text-secondary">Hours</span>
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400 text-sm md:text-lg">{providerStats.totalHours}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs md:text-sm text-gray-600 dark:text-dark-text-secondary">Avg Duration</span>
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400 text-sm md:text-lg">
                      {Math.round(providerStats.totalHours / providerStats.completedJobs * 10) / 10} hrs
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs md:text-sm text-gray-600 dark:text-dark-text-secondary">Clients</span>
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400 text-sm md:text-lg">{providerStats.activeClients}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-3 gap-1 md:gap-4 mb-6">
              <button 
                onClick={() => setCurrentView('schedule')}
                className="bg-green-700 text-white p-2 md:p-4 rounded-lg hover:bg-green-800 transition-colors flex flex-col md:flex-row items-center justify-center space-x-0 md:space-x-2 space-y-1 md:space-y-0"
              >
                <Calendar className="w-3 h-3 md:w-5 md:h-5" />
                <span className="text-xs md:text-sm">Schedule</span>
              </button>
              <button 
                onClick={() => setCurrentView('clients')}
                className="bg-green-700 text-white p-2 md:p-4 rounded-lg hover:bg-green-800 transition-colors flex flex-col md:flex-row items-center justify-center space-x-0 md:space-x-2 space-y-1 md:space-y-0"
              >
                <Users className="w-3 h-3 md:w-5 md:h-5" />
                <span className="text-xs md:text-sm">Clients</span>
              </button>
              <button 
                onClick={() => setCurrentView('performance')}
                className="bg-green-700 text-white p-2 md:p-4 rounded-lg hover:bg-green-800 transition-colors flex flex-col md:flex-row items-center justify-center space-x-0 md:space-x-2 space-y-1 md:space-y-0"
              >
                <TrendingUp className="w-3 h-3 md:w-5 md:h-5" />
                <span className="text-xs md:text-sm">Reports</span>
              </button>
            </div>

            {/* Recent Jobs */}
            <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-2 md:p-6">
              <h2 className="text-sm md:text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-2 md:mb-4">Recent Jobs</h2>
              <div className="max-h-96 overflow-y-auto">
                <div className="grid grid-cols-3 gap-1 md:gap-3">
                  {recentJobs.map((job) => (
                    <div key={job.id} className="border border-gray-200 dark:border-dark-border rounded-lg p-1 md:p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <div className="text-center">
                        <h3 className="font-medium text-gray-900 dark:text-dark-text-primary text-xs mb-1 truncate">{job.clientName}</h3>
                        <p className="text-xs text-gray-600 dark:text-dark-text-secondary mb-1 truncate">{job.service}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{job.date}</p>
                        <div className={`px-1 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(job.status)}`}>
                          {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : (
          renderCurrentView()
        )}
      </main>
    </div>
  )
}
